import i18n from '@/plugins/i18n';

/* Texto de tela que troca com o idioma: lido a cada render, sem mudar quem usa a constante. */
const uiText = (key) => i18n.t(`comum.generalConstantsUi.${key}`);

/* Estado civil e tipo de documento: a `description` fica em português porque a
   entrega de terceiro salva o texto (item-value="description") e o documento de
   autorização sai com ele. A tela usa `displayLabel`, que não é enumerável — não
   entra em spread nem no JSON enviado ao backend. */
const withDisplayLabel = (item, key) =>
  Object.defineProperty(item, 'displayLabel', { get: () => uiText(key), enumerable: false });

const PROFILES = [
  { id: 1, description: 'Administrador' },
  { id: 2, description: 'Corban' },
  { id: 3, description: 'Cliente' }
];

/* As 27 UFs ficam aqui, não no IBGE: quando o IBGE caía o campo Estado vinha vazio e ninguém conseguia cadastrar. */
const BRAZILIAN_STATES = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'
];

const REGISTER_TYPE = {
  EXCHANGE: { id: 1, get description() { return uiText('registerType.exchange'); } },
  BASIC_REMITTANCE: { id: 2, get description() { return uiText('registerType.basicRemittance'); } },
  COMPLETE_REMITTANCE: { id: 3, get description() { return uiText('registerType.completeRemittance'); } },
  LEGAL_PERSON: { id: 4, get description() { return uiText('registerType.legalPerson'); } }
};

// In the future, save this information in database

const PERSON_TYPE = [
  { id: 1, get description() { return uiText('personType.legalPerson'); } },
  { id: 2, get description() { return uiText('personType.naturalPerson'); } }
];

const MARITAL_STATUS = {
  SINGLE: withDisplayLabel({ id: 1, description: 'Solteiro(a)' }, 'maritalStatus.single'),
  MARRIED: withDisplayLabel({ id: 2, description: 'Casado(a)' }, 'maritalStatus.married'),
  DIVORCED: withDisplayLabel({ id: 3, description: 'Divorciado(a)' }, 'maritalStatus.divorced'),
  STABLE_UNION: withDisplayLabel({ id: 4, description: 'União estável' }, 'maritalStatus.stableUnion'),
  WIDOWED: withDisplayLabel({ id: 5, description: 'Viúvo(a)' }, 'maritalStatus.widowed')
};

const MARRIAGE_SYSTEM = {
  PARTIAL_COMMUNION_OF_GODS: { id: 1, get description() { return uiText('marriageSystem.partialCommunion'); } },
  UNIVERSAL_COMMUNION_OF_GODS: { id: 2, get description() { return uiText('marriageSystem.universalCommunion'); } },
  STABLE_UNION: { id: 3, get description() { return uiText('marriageSystem.stableUnion'); } },
  SEPARATION_OF_GODS: { id: 4, get description() { return uiText('marriageSystem.separation'); } },
  SEPARATION_OF_GODS_FREEDOM_OF_CHOICE: {
    id: 5,
    get description() { return uiText('marriageSystem.separationFreedomOfChoice'); }
  },
  PARTIAL_COMMUNION_OF_GODS_JUSTICE_AND_UNION: {
    id: 6,
    get description() { return uiText('marriageSystem.partialCommunionJusticeAndUnion'); }
  }
};

import { DOCUMENT_TYPE } from '../../../common/constants/generic/customerDocuments.js';

const IDENTIFICATION_DOCUMENT_TYPE = {
  RG: withDisplayLabel({ id: 1, description: 'RG' }, 'identificationDocumentType.rg'),
  CNH: withDisplayLabel({ id: 2, description: 'CNH' }, 'identificationDocumentType.cnh'),
  Funcional: withDisplayLabel({ id: 3, description: 'Funcional' }, 'identificationDocumentType.functional'),
  'RNE/RNM': withDisplayLabel({ id: 4, description: 'RNE/RNM' }, 'identificationDocumentType.rneRnm'),
  Passaporte: withDisplayLabel({ id: 5, description: 'Passaporte' }, 'identificationDocumentType.passport'),
  DNI: withDisplayLabel({ id: 6, description: 'DNI ou cédula estrangeira' }, 'identificationDocumentType.dniCedula')
};

const LIST_IDENTIFICATION_DOCUMENT_TYPE = [
  IDENTIFICATION_DOCUMENT_TYPE.RG,
  IDENTIFICATION_DOCUMENT_TYPE.CNH,
  IDENTIFICATION_DOCUMENT_TYPE.Funcional,
  IDENTIFICATION_DOCUMENT_TYPE['RNE/RNM'],
  IDENTIFICATION_DOCUMENT_TYPE.Passaporte
  // DNI/Cédula Estrangeira (id=6) não está nesta lista — exibido apenas no ramo !bornInBrazil
  // de availableIdentificationDocumentTypes em PFExchangeBasicCompleteRemittanceFields.vue.
  // Incluí-lo aqui fazia o DNI aparecer no dropdown de brasileiros e em OperationExchangeDeliveryAddress.
];

// const NATIONAL_BANKS = [
//   { 'id': 1, 'description': 'national bank 1' },
//   { 'id': 2, 'description': 'national bank 2' },
// ];

// const INTERNATIONAL_BANKS = [
//   { 'id': 1, 'description': 'international bank 1' },
//   { 'id': 2, 'description': 'international bank 2' },
// ];

const FORM_OF_INCOPORATION = [
  { id: 1, description: 'MEI' },
  { id: 2, description: 'ME' },
  { id: 3, description: 'Simples Nacional' },
  { id: 4, description: 'LTDA' },
  { id: 5, description: 'S/A' }
];

// Helper IDs for specific forms of incorporation
const SA_FORM_OF_INCOPORATION_ID = FORM_OF_INCOPORATION.find(
  (item) => item.description === 'S/A'
)?.id;

const OPERATING_MODE = [
  { id: 1, get description() { return uiText('operatingMode.ownAccount'); } },
  {
    id: 2,
    get description() { return uiText('operatingMode.onBehalfOfThirdParties'); }
  }
];

const HABILITATION_MODE = [
  { id: 1, get description() { return uiText('habilitationMode.limited'); } },
  { id: 2, get description() { return uiText('habilitationMode.unlimited'); } },
  { id: 3, get description() { return uiText('habilitationMode.express'); } }
];

const PAYMENT_MODE = [
  { id: 1, get description() { return uiText('paymentMode.advance'); } },
  { id: 2, get description() { return uiText('paymentMode.ready'); } },
  { id: 3, get description() { return uiText('paymentMode.both'); } }
];

const BUSINESS_PURPOSE_TYPE = [
  { id: 1, get description() { return uiText('businessPurposeType.commercial'); } },
  {
    id: 2,
    get description() { return uiText('businessPurposeType.unilateralTransfersAndTourism'); }
  },
  { id: 3, get description() { return uiText('businessPurposeType.financial'); } },
  { id: 4, get description() { return uiText('businessPurposeType.nonResidentAccount'); } },
  { id: 5, get description() { return uiText('businessPurposeType.hedgeAndDerivatives'); } },
  { id: 6, get description() { return uiText('businessPurposeType.foreignCurrencyAccount'); } },
  { id: 7, get description() { return uiText('businessPurposeType.otherProducts'); } }
];

const CORRESPONDING = [
  { id: 0, name: 'João da Silva' },
  { id: 1, name: 'Maria da Silva' },
  { id: 2, name: 'Pedro da Silva' }
];

const BANKS = [
  { id: 0, description: 'Banco BS2' },
  { id: 1, description: 'Banco Ourinvest' },
  { id: 2, description: 'Treviso Corretora de Câmbio S/A' }
];

const ACCESS_ROUTES = {
  DASHBOARD: {
    description: 'Dashboard',
    profiles: [1, 2]
  },
  USERS: {
    description: 'Usuários',
    profiles: [1]
  }
};

const ASSETS = [
  { id: 0, get description() { return uiText('assets.upTo100Thousand'); } },
  { id: 1, get description() { return uiText('assets.upTo300Thousand'); } },
  { id: 2, get description() { return uiText('assets.upTo500Thousand'); } },
  { id: 3, get description() { return uiText('assets.upTo1Million'); } },
  { id: 4, get description() { return uiText('assets.over1Million'); } }
];

const LIQUIDATION = [
  { id: 1, description: 'D+0/D+0' },
  { id: 2, description: 'D+0/D+1' },
  { id: 3, description: 'D+0/D+2' },
  { id: 4, description: 'D+1/D+1' },
  { id: 5, description: 'D+1/D+2' },
  { id: 6, description: 'D+2/D+2' }
];

// Opções de liquidação para operações de entrada (compra de moeda estrangeira).
// ME (moeda estrangeira) é sempre D+0 — dinheiro já está na conta do banco.
// BRL pode ser pago em datas futuras conforme interesse do cliente.
const LIQUIDATION_ENTRY = [
  { id: 1, description: 'D+0/D+0' },
  { id: 7, description: 'D+1/D+0' },
  { id: 8, description: 'D+2/D+0' },
  { id: 9, description: 'D+3/D+0' }
];

/* Tipo de documento da operação: a `description` entra no nome do arquivo baixado e vai
   ao backend, então fica em português; a tela usa `displayLabel` (não enumerável). */
const withOperationDocumentLabel = (item, key) =>
  Object.defineProperty(item, 'displayLabel', {
    get: () => i18n.t(key),
    enumerable: false
  });

const OPERATION_DOCUMENT_TYPES = {
  DIDUE: withOperationDocumentLabel({
    id: 1,
    description: 'DUIMP/DU-E'
  }, 'comum.operationDocumentTypesUi.didue'),
  SWIFT: withOperationDocumentLabel({
    id: 2,
    description: 'SWIFT'
  }, 'comum.operationDocumentTypesUi.swift'),
  CC: withOperationDocumentLabel({
    id: 3,
    description: 'Contrato de Câmbio'
  }, 'comum.operationDocumentTypesUi.exchangeContract'),
  COMPROVANTE: withOperationDocumentLabel({
    id: 4,
    description: 'Comprovante de Pagamento'
  }, 'comum.operationDocumentTypesUi.paymentReceipt'),
  OUTROS: withOperationDocumentLabel({
    id: 5,
    description: 'Outros'
  }, 'comum.operationDocumentTypesUi.others'),
  BOLETO_BANCARIO: withOperationDocumentLabel({
    id: 6,
    description: 'Boleto Bancário'
  }, 'comum.operationDocumentTypesUi.bankSlip'),
  AUTORIZACAO: withOperationDocumentLabel({
    id: 7,
    description: 'Autorização de entrega'
  }, 'comum.operationDocumentTypesUi.deliveryAuthorization'),
  INVOICE: withOperationDocumentLabel({
    id: 8,
    description: 'Invoice'
  }, 'comum.operationDocumentTypesUi.invoice'),
  BLAWB: withOperationDocumentLabel({
    id: 9,
    description: 'BL / AWB'
  }, 'comum.operationDocumentTypesUi.blAwb'),
  CONTRATOS_DIVERSOS: withOperationDocumentLabel({
    id: 10,
    description: 'Contratos diversos'
  }, 'comum.operationDocumentTypesUi.miscContracts'),
  CONTRATO_HEDGE: withOperationDocumentLabel({
    id: 11,
    description: 'Contrato de Hedge'
  }, 'comum.operationDocumentTypesUi.hedgeContract'),
  PROPOSTA_OPERACAO: withOperationDocumentLabel({
    id: 12,
    description: 'Proposta de Operação'
  }, 'comum.operationDocumentTypesUi.operationProposal')
};

const OPERATION_DOCUMENT_TYPE_LIST = Object.values(OPERATION_DOCUMENT_TYPES);

const EXCHANGE_TOURISM_DOCUMENT_TYPES = [
  OPERATION_DOCUMENT_TYPES.PROPOSTA_OPERACAO,
  OPERATION_DOCUMENT_TYPES.COMPROVANTE,
  OPERATION_DOCUMENT_TYPES.AUTORIZACAO,
  OPERATION_DOCUMENT_TYPES.OUTROS
];

const AUTHORIZATION_TYPE = {
  PER_OPERATION: { id: 1, get description() { return uiText('authorizationType.perOperation'); } },
  SIX_MONTHS: { id: 2, get description() { return uiText('authorizationType.sixMonths'); } }
};

const AUTHORIZATION_TYPE_LIST = Object.values(AUTHORIZATION_TYPE);

const OPERATION_TYPE = {
  REMMITANCE: {
    id: '1',
    description: 'Remmitance'
  },
  EXCHANGE: {
    id: '2',
    description: 'Exchange'
  },
  BOTH: {
    id: '3',
    description: 'Both'
  }
};

const OPERATION_CALCULATION_TYPE = {
  CURRENCY_AMOUNT: {
    id: '1',
    get description() { return uiText('calculationType.currencyAmount'); }
  },
  BRL_AMOUNT: {
    id: '2',
    get description() { return uiText('calculationType.brlAmount'); }
  }
};

const DELIVERY_TYPE = {
  WITHDRAWAL: {
    id: '1',
    description: 'Retirada na Loja'
  },
  DELIVERY: {
    id: '2',
    description: 'Delivery'
  },
  BOTH: {
    id: '3',
    description: 'Ambas'
  }
};

const PAYMENT_METHOD = {
  PIX: {
    id: 1,
    description: 'Pix'
  },
  TED_DOC: {
    id: 2,
    description: 'TED/DOC'
  },
  BOLETO: {
    id: 3,
    description: 'Boleto'
  },
  CREDIT_CARD: {
    id: 4,
    description: 'Cartão de Crédito'
  },
  CASH: {
    id: 5,
    description: 'Dinheiro'
  }
};

const CUSTOMER_EXCHANGE_BANK_STATUS_CORES = {
  CADASTRO_ENVIADO: 'customerExchangeBankWorkflowStatusCadastroEnviado',
  CADASTRO_ANDAMENTO: 'customerExchangeBankWorkflowStatusCadastroEmAndamento',
  CADASTRO_FINALIZADO: 'customerExchangeBankWorkflowStatusCadastroFinalizado',
  CADASTRO_ANALISE: 'customerExchangeBankWorkflowStatusCadastroEmAnalise',
  CADASTRO_PENDENCIA_DOCUMENTAL: 'customerExchangeBankWorkflowStatusPendenciaDocumental',
  ENVIADO_AO_BANCO: 'customerExchangeBankWorkflowStatusEnviadoAoBanco',
  PLD_COMPLIANCE: 'customerExchangeBankWorkflowStatusPldCompliance',
  CADASTRO_APROVADO: 'customerExchangeBankWorkflowStatusCadastroAprovado',
  CADASTRO_ENCERRADO: 'customerExchangeBankWorkflowStatusEncerrado',
  CADASTRO_RECUSADO: 'customerExchangeBankWorkflowStatusCadastroRecusado',
  ATUALIZACAO_CADASTRAL: 'customerExchangeBankWorkflowStatusAtualizacaoCadastral',
  PENDENCIA_BANCO: 'customerExchangeBankWorkflowStatusPendenciaBanco'
};

const COLUMS_CUSTOMER_EXCHANGE_BANKS = [
  {
    get text() { return uiText('customerExchangeBanksColumns.createdAt'); },
    value: 'customer.createdAt',
    align: 'left',
    sortable: true,
    width: '150px'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.updatedAt'); },
    value: 'customer.updatedAt',
    align: 'left',
    sortable: true,
    width: '150px'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.name'); },
    value: 'customer.customerNameOrCompanyName',
    align: 'left',
    sortable: true,
    width: '300px'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.registerProfile'); },
    value: 'customer.registerType.description',
    align: 'left',
    sortable: true,
    width: '150px'
  },
  {
    get text() { return i18n.t('common.fields.bank'); },
    value: 'bank.companyName',
    align: 'left',
    sortable: true,
    width: '200px'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.corban'); },
    value: 'customer.correspondent.name',
    align: 'left',
    sortable: true,
    width: '170px'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.email'); },
    value: 'customer.email',
    align: 'left',
    sortable: true,
    width: '200px',
    cellClass: 'email-column'
  },
  {
    get text() { return uiText('customerExchangeBanksColumns.flow'); },
    value: 'statusDescription',
    align: 'center',
    width: '190px'
  },
  { get text() { return uiText('customerExchangeBanksColumns.actions'); }, value: 'actions', align: 'center', sortable: false, width: '70px' }
];

const FILE_TYPE = {
  CUSTOMER_DOCUMENT: 'customerDocument',
  CUSTOMER_OPERATIONAL_DOCUMENT: 'customerOperationalDocument',
  OPERATION_DOCUMENT: 'operationDocument'
};

const TEMPLATE_TYPES = {
  EMAIL: {
    id: '1',
    description: 'E-mails'
  },
  OUTROS: {
    id: '3',
    description: 'Outros'
  },
  FICHA_CLIENTE: {
    id: '4',
    description: 'Ficha do Cliente'
  },
  MENSAGEM_OPERACAO: {
    id: '5',
    description: 'Mensagem de Operação'
  },
  DOCUMENTOS_EMITIDOS: {
    id: '6',
    description: 'Documentos Emitidos'
  }
};

const PAGINATION_DEFAULT = 30;
const ITEMS_PER_PAGE_DEFAULT = { itemsPerPageOptions: [5, 10, 30, 50, -1] };

const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CHF',
  'CAD',
  'AUD',
  'NZD',
  'CNY',
  'HKD',
  'SGD',
  'MXN',
  'ARS',
  'UYU',
  'CLP',
  'PEN',
  'BOB',
  'COP',
  'ZAR',
  'AED'
];

const HOME_EXCHANGE_SPREADS = [
  { code: 'USD', spreadBuy: 4.38, spreadSell: 6.5 },
  { code: 'EUR', spreadBuy: 4.58, spreadSell: 6.7 },
  { code: 'GBP', spreadBuy: 9.38, spreadSell: 10.9 },
  { code: 'CAD', spreadBuy: 14.38, spreadSell: 10.9 },
  { code: 'AUD', spreadBuy: 13.38, spreadSell: 11.0 },
  { code: 'CLP', spreadBuy: 20.38, spreadSell: 23.5 },
  { code: 'CHF', spreadBuy: 7.88, spreadSell: 19.5 },
  { code: 'MXN', spreadBuy: 24.88, spreadSell: 21.8 }
];

const EMAIL_SENT_STATUS_ID = 1;

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
};

export {
  ACCESS_ROUTES,
  PROFILES,
  REGISTER_TYPE,
  PERSON_TYPE,
  MARITAL_STATUS,
  MARRIAGE_SYSTEM,
  DOCUMENT_TYPE,
  IDENTIFICATION_DOCUMENT_TYPE,
  LIST_IDENTIFICATION_DOCUMENT_TYPE,
  CORRESPONDING,
  BANKS,
  FORM_OF_INCOPORATION,
  SA_FORM_OF_INCOPORATION_ID,
  BUSINESS_PURPOSE_TYPE,
  PAYMENT_MODE,
  HABILITATION_MODE,
  OPERATING_MODE,
  ASSETS,
  LIQUIDATION,
  LIQUIDATION_ENTRY,
  OPERATION_DOCUMENT_TYPE_LIST,
  OPERATION_DOCUMENT_TYPES,
  EXCHANGE_TOURISM_DOCUMENT_TYPES,
  AUTHORIZATION_TYPE,
  AUTHORIZATION_TYPE_LIST,
  OPERATION_TYPE,
  OPERATION_CALCULATION_TYPE,
  DELIVERY_TYPE,
  PAYMENT_METHOD,
  CUSTOMER_EXCHANGE_BANK_STATUS_CORES,
  FILE_TYPE,
  TEMPLATE_TYPES,
  PAGINATION_DEFAULT,
  ITEMS_PER_PAGE_DEFAULT,
  COLUMS_CUSTOMER_EXCHANGE_BANKS,
  CURRENCIES,
  HOME_EXCHANGE_SPREADS,
  EMAIL_SENT_STATUS_ID,
  HTTP_STATUS,
  BRAZILIAN_STATES
};
